import React from 'react';
import { usePreferences } from '../../hooks/usePreferences';
import {
    Grid,
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Divider,
    Box,
    Button,
    Switch,
    FormControlLabel
} from '@mui/material';
import { Field } from 'formik';
import {
    TIMEZONE_OPTIONS,
    CURRENCY_OPTIONS,
    DATE_FORMAT_OPTIONS,
    TIME_FORMAT_OPTIONS,
    POSTAL_CODE_FORMATS,
    INVOICE_FORMAT_OPTIONS,
    detectSystemTimezone
} from '../../utils/preferencesOptions';

/**
 * BusinessPreferencesForm Component
 * 
 * Renders form fields for tenant-level (business) preferences.
 * These are the default values that all locations inherit from.
 * Unlike LocationPreferences, there's no inheritance toggle—these are the source defaults.
 * 
 * Organized in logical groups:
 * - Regional Settings (phone format, timezone, postal code)
 * - Business Settings (currency, date format)
 * - Document Formats (invoice, prescription number patterns)
 */

// const CURRENCY_POSITION_OPTIONS = [
//     { value: 'before', label: 'Before amount ($100)' },
//     { value: 'after', label: 'After amount (100$)' }
// ];

// const DECIMAL_PLACES_OPTIONS = [
//     { value: 0, label: '0' },
//     { value: 1, label: '1' },
//     { value: 2, label: '2' },
//     { value: 3, label: '3' }
// ];


// const PRESCRIPTION_FORMAT_OPTIONS = [
//     { value: 'RX-{YYYY}-{####}', label: 'RX-2025-0001 (Standard)' },
//     { value: '{STORE}RX{YYYY}{####}', label: 'ARX20250001 (Compact)' },
//     { value: 'RX{YY}{#####}', label: 'RX2500001 (Short)' },
//     { value: 'custom', label: 'Custom Format...' }
// ];

const BusinessPreferencesForm = ({ values }) => {
    const [showCustomInvoiceFormat, setShowCustomInvoiceFormat] = React.useState(false);
    const [detectedTimezone, setDetectedTimezone] = React.useState(null);
    const { preferences } = usePreferences();
    const isManualMode = preferences?.opticalEntryMode === 'manual';

    // Check if current value is a custom format (not in predefined list)
    React.useEffect(() => {
        const invoiceFormat = values.preferences?.invoiceNumberFormat || '';
        const isCustomInvoice = invoiceFormat && !INVOICE_FORMAT_OPTIONS.some(opt => opt.value === invoiceFormat);
        setShowCustomInvoiceFormat(isCustomInvoice);

    }, [values.preferences?.invoiceNumberFormat]);

    /**
     * Render a preference field
     */
    const renderField = (config) => {
        const {
            key,
            label,
            type = 'text',
            options = [],
            helperText,
            gridSize = 6,
            showCustom,
            setShowCustom
        } = config;

        const fieldName = `preferences.${key}`;
        const currentValue = values.preferences?.[key] || '';
        const displayValue = showCustom ? 'custom' : currentValue;

        return (
            <Grid item xs={12} sm={gridSize} key={key}>
                <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {label}
                        </Typography>
                        {key === 'timezone' && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    const detected = detectSystemTimezone();
                                    if (detected) {
                                        setDetectedTimezone(detected);
                                    }
                                }}
                                sx={{ ml: 1 }}
                            >
                                Auto-detect
                            </Button>
                        )}
                    </Box>

                    {type === 'select' ? (
                        <>
                            <Field name={fieldName}>
                                {({ field, form }) => (
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={displayValue}
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (selectedValue === 'custom') {
                                                    setShowCustom && setShowCustom(true);
                                                } else {
                                                    setShowCustom && setShowCustom(false);
                                                    form.setFieldValue(fieldName, selectedValue);
                                                }
                                            }}
                                            displayEmpty
                                        >
                                            {options.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Field>
                            
                            {showCustom && (
                                <Field name={fieldName}>
                                    {({ field, form }) => (
                                        <TextField
                                            {...field}
                                            size="small"
                                            fullWidth
                                            placeholder="Enter custom format"
                                            sx={{ mt: 1 }}
                                            value={currentValue}
                                            onChange={(e) => form.setFieldValue(fieldName, e.target.value)}
                                        />
                                    )}
                                </Field>
                            )}
                        </>
                    ) : (
                        <Field
                            as={TextField}
                            name={fieldName}
                            size="small"
                            fullWidth
                            value={currentValue}
                        />
                    )}

                    {detectedTimezone && key === 'timezone' && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                                ✓ Detected: {detectedTimezone.label}
                            </Typography>
                            <Box display="flex" gap={1}>
                                <Field name={fieldName}>
                                    {({ field, form }) => (
                                        <>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    form.setFieldValue(fieldName, detectedTimezone.value);
                                                    setDetectedTimezone(null);
                                                }}
                                            >
                                                Apply
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setDetectedTimezone(null)}
                                            >
                                                Dismiss
                                            </Button>
                                        </>
                                    )}
                                </Field>
                            </Box>
                        </Box>
                    )}

                    {helperText && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                            {helperText}
                        </Typography>
                    )}

                    {key === 'invoiceNumberFormat' && currentValue && !currentValue.includes('{TYPE}') && (
                        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                            Include {'{TYPE}'} to clearly differentiate quote numbers from invoice numbers.
                        </Typography>
                    )}
                </Box>
            </Grid>
        );
    };

    // Field configurations organized by section
    const regionalFields = [
        {
            key: 'phoneNumberFormat',
            label: 'Phone Number Format',
            helperText: 'Format for displaying phone numbers (# = digit)',
            gridSize: 6
        },
        {
            key: 'phoneNumberCountryCode',
            label: 'Country Code',
            helperText: 'Default country code for phone numbers (e.g., +1)',
            gridSize: 6
        },
        {
            key: 'timezone',
            label: 'Timezone',
            type: 'select',
            options: TIMEZONE_OPTIONS,
            helperText: 'Default timezone for all locations',
            gridSize: 6
        },
        {
            key: 'postalCodeFormat',
            label: 'Postal Code Format',
            type: 'select',
            options: POSTAL_CODE_FORMATS,
            helperText: 'Format for postal/ZIP codes',
            gridSize: 6
        }
    ];

    const businessFields = [
        {
            key: 'currency',
            label: 'Currency',
            type: 'select',
            options: CURRENCY_OPTIONS,
            helperText: 'ISO 4217 currency code',
            gridSize: 6
        },
        {
            key: 'currencySymbol',
            label: 'Currency Symbol',
            helperText: 'e.g., $, €, £',
            gridSize: 6
        },
        // {
        //     key: 'currencyPosition',
        //     label: 'Currency Position',
        //     type: 'select',
        //     options: CURRENCY_POSITION_OPTIONS,
        //     gridSize: 6
        // },
        // {
        //     key: 'decimalPlaces',
        //     label: 'Decimal Places',
        //     type: 'select',
        //     options: DECIMAL_PLACES_OPTIONS,
        //     helperText: 'Number of decimal places for currency amounts',
        //     gridSize: 6
        // },
        // {
        //     key: 'thousandsSeparator',
        //     label: 'Thousands Separator',
        //     helperText: 'Character for separating thousands (comma, period, space)',
        //     gridSize: 6
        // },
        // {
        //     key: 'decimalSeparator',
        //     label: 'Decimal Separator',
        //     helperText: 'Character for decimal point',
        //     gridSize: 6
        // },
        {
            key: 'dateFormat',
            label: 'Date Format',
            type: 'select',
            options: DATE_FORMAT_OPTIONS,
            gridSize: 6
        },
        {
            key: 'timeFormat',
            label: 'Time Format',
            type: 'select',
            options: TIME_FORMAT_OPTIONS,
            gridSize: 6
        }
    ];

    const documentFields = [
        {
            key: 'invoiceNumberFormat',
            label: 'Invoice Number Format',
            type: 'select',
            options: INVOICE_FORMAT_OPTIONS,
            helperText: 'Tokens: {YYYY}=2025, {YY}=25, {STORE}=A, {#######}=0000001, {TYPE}=INV or Q. Use {TYPE} to differentiate quote and invoice numbers. Format must include {STORE}.',
            gridSize: 6,
            showCustom: showCustomInvoiceFormat,
            setShowCustom: setShowCustomInvoiceFormat
        },
        // {
        //     key: 'prescriptionNumberFormat',
        //     label: 'Prescription Number Format',
        //     type: 'select',
        //     options: PRESCRIPTION_FORMAT_OPTIONS,
        //     helperText: 'Tokens: {YYYY}=2025, {YY}=25, {STORE}=A, {####}=0001',
        //     gridSize: 6,
        //     showCustom: showCustomPrescriptionFormat,
        //     setShowCustom: setShowCustomPrescriptionFormat
        // }
    ];

    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            {/* Information Box */}
            <Box sx={{
                p: 2,
                mb: 3,
                backgroundColor: 'info.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light'
            }}>
                <Typography variant="caption" color="textSecondary">
                    <strong>💡 Tip:</strong> These are your business-wide defaults. All locations will inherit 
                    these settings unless they override them with location-specific preferences.
                </Typography>
            </Box>

            {/* Regional Settings */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Regional Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {regionalFields.map(renderField)}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Business Settings */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Business Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {businessFields.map(renderField)}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Document Formats */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Document Formats
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {documentFields.map(renderField)}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Billing Settings */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Billing Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <Field name="preferences.allowCloseWithBalance">
                        {({ field, form }) => (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(field.value)}
                                            onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                        />
                                    }
                                    label="Allow closing invoices with an outstanding balance"
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, ml: 6 }}>
                                    When enabled, invoices can be dispensed even if a balance is still owed.
                                    Use this for practices that write off small balances or handle insurance adjustments after closing.
                                </Typography>
                            </Box>
                        )}
                    </Field>
                </Grid>
            </Grid>

            {/* Invoice Settings for force invoice delete and invoice retention days */}
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                Invoice Settings
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <Field name="preferences.allowInvoiceForceDelete">
                        {({ field, form }) => (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(field.value)}
                                            onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                        />
                                    }
                                    label="Allow Force Deletion of Invoices"
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, ml: 6 }}>
                                    When enabled, allows permanent deletion of invoices and all associated records (payments, adjustments, etc.) without soft-deletion.
                                </Typography>
                            </Box>
                        )}
                    </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Field name="preferences.deletedInvoiceRetentionDays">
                        {({ field, form }) => (
                            <TextField
                                {...field}
                                type="number"
                                label="Deleted Invoice Retention Days"
                                fullWidth
                                size="small"
                                InputProps={{ inputProps: { min: 0 } }}
                                helperText="Number of days to retain deleted invoice records before permanent purge. Only applicable if force delete is disabled."
                            />
                        )}
                    </Field>
                </Grid>
                {/* flag for allowing an invoice after return */}
                <Grid item xs={12} sm={6}>
                    <Field name="preferences.allowEditingInvoiceAfterReturn">
                        {({ field, form }) => (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(field.value)}
                                            onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                        />
                                    }
                                    label="Allow Editing Invoice After Return"
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, ml: 6 }}>
                                    When enabled, allows editing of invoices after return.
                                </Typography>
                            </Box>
                        )}
                    </Field>
                </Grid>

                {/* flag for allowing edit of voided invoices */}
                <Grid item xs={12} sm={6}>
                    <Field name="preferences.allowVoidedInvoiceEditing">
                        {({ field, form }) => (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(field.value)}
                                            onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                        />
                                    }
                                    label="Allow Editing of Voided Invoices"
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, ml: 6 }}>
                                    When enabled, allows editing of invoices that have been voided.
                                </Typography>
                            </Box>
                        )}
                    </Field>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            {/* Inventory Settings */}
            {!isManualMode && (
                <>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                        Inventory Settings
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <Field name="preferences.lowStockQuantityThreshold">
                                {({ field, form }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        label="Low Stock Quantity Threshold"
                                        fullWidth
                                        size="small"
                                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                                        helperText="Inventory quantity at or below this value is considered low stock. Cannot be negative."
                                        onChange={(e) => {
                                            const nextValue = e.target.value;
                                            form.setFieldValue(field.name, nextValue === '' ? '' : Number(nextValue));
                                        }}
                                    />
                                )}
                            </Field>
                        </Grid>
                    </Grid>
                </>
            )}


        </Box>
    );
};

export default BusinessPreferencesForm;
